"use client";

import { useMemo } from "react";
import { getDefinition, resolveImageFilename } from "@/lib/catalog";
import { chargerEngineDetection } from "@/lib/diagnostics/graph";
import { formatAmp } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { batteryRuntime, DEFAULT_SOC, formatHours } from "@/lib/simulate/battery-runtime";
import { getSimulateStateSpec, hasEngineShutdownDetection, hasManualCombinerStates } from "@/lib/simulate/states";
import { useProductImageVisibility } from "@/lib/settings/product-image-visibility";
import { useSystemStore } from "@/lib/store";
import { catalogImageUrl } from "@/lib/supabase/client";
import type { ComponentDefinition } from "@/lib/types";
import { useCanvasBatteryDetails, useCanvasViewOnly } from "./canvas-mode";
import { rvPanelBranchCount } from "@/lib/catalog/rv-distribution";
import { RvPanelSwitches } from "../inspector/sections/rv-panel-controls";
import { computeAcAvailability } from "@/lib/ac-live";
import { hasAcPanelControls } from "@/lib/ac-panel-controls";
import { isAcTerminalId } from "@/lib/ac";
import { AcPanelSwitches } from "./ac-panel-switches";

export function SimulateOverlay({
  nodeId,
  def,
  displayName,
}: {
  nodeId: string;
  def: ComponentDefinition;
  displayName: string;
}) {
  const mode = useSystemStore((s) => s.mode);
  const showBatteryDetails = useCanvasBatteryDetails();
  if (mode !== "simulate" || !showBatteryDetails) return null;
  if (def.category === "battery") {
    return <BatterySocPanel nodeId={nodeId} def={def} displayName={displayName} />;
  }
  return null;
}

export function LoadActivePills({ nodeId }: { nodeId: string }) {
  const tr = useT();
  const active = useSystemStore(
    (s) => s.simulateState.loadStates[nodeId]?.active ?? true,
  );
  const setLoadActive = useSystemStore((s) => s.setLoadActive);
  const options: Array<{ id: "on" | "off"; label: string; active: boolean }> = [
    { id: "on", label: tr("simulate.load_on"), active },
    { id: "off", label: tr("simulate.load_off"), active: !active },
  ];
  return (
    <>
      <SegmentedStatePills
        options={options.map((o) => ({ id: o.id, label: o.label, active: o.active }))}
        onSelect={(id) => setLoadActive(nodeId, id === "on")}
      />
      <AcSupplyIndicator nodeId={nodeId} />
    </>
  );
}

export function SimulateStatePills({
  nodeId,
  def,
}: {
  nodeId: string;
  def: ComponentDefinition;
}) {
  const tr = useT();
  const spec = getSimulateStateSpec(def);
  const activeId = useSystemStore(
    (s) => s.simulateState.componentStates[nodeId],
  );
  const loadActive = useSystemStore(
    (s) => s.simulateState.loadStates[nodeId]?.active ?? true,
  );
  const setComponentSimulateState = useSystemStore(
    (s) => s.setComponentSimulateState,
  );
  if (!spec) return null;
  if (rvPanelBranchCount(def) != null) return <><RvPanelSwitches nodeId={nodeId} def={def} /><AcSupplyIndicator nodeId={nodeId} /></>;
  if (hasAcPanelControls(def)) return <><AcPanelSwitches nodeId={nodeId} def={def} /><AcSupplyIndicator nodeId={nodeId} /></>;
  if (def.id === "dist-hughes-pwd50-epo-h" && spec.options.length === 1) return <AcSupplyIndicator nodeId={nodeId} />;
  // A previous generic load toggle may still be stored after replacing a
  // definition. Display its effective off state until a declared option is chosen.
  const current = def.category === "load" && !loadActive
    ? spec.options.find(o => o.ampOverride === 0 || o.ampMultiplier === 0)?.id
    : activeId ?? spec.default;
  const engineDetection = hasEngineShutdownDetection(def);
  const manualCombiner = hasManualCombinerStates(def);
  return (
    <>
      <SegmentedStatePills
        options={spec.options.map((o) => ({
          id: o.id,
          label: engineDetection && (o.id === "on" || o.id === "forced" || o.id === "off")
            ? tr(`simulate.orion.${o.id}`)
            : manualCombiner && (o.id === "combined" || o.id === "isolated")
              ? tr(`simulate.combiner.${o.id}`) : o.label,
          title: engineDetection && o.id === "forced" ? tr("simulate.orion.forced_help")
            : manualCombiner ? tr("simulate.combiner.help") : undefined,
          active: o.id === current,
        }))}
        onSelect={(id) => setComponentSimulateState(nodeId, id)}
      />
      {engineDetection && <ChargerEngineStatus nodeId={nodeId} def={def} />}
      {manualCombiner && <div className="mt-1 px-2 text-center text-[10px] leading-tight text-fg">{tr("simulate.combiner.help")}</div>}
      <AcSupplyIndicator nodeId={nodeId} />
    </>
  );
}

function AcSupplyIndicator({ nodeId }: { nodeId: string }) {
  const tr = useT();
  const system = useSystemStore(s => s.system);
  const componentStates = useSystemStore(s => s.simulateState.componentStates);
  const loadStates = useSystemStore(s => s.simulateState.loadStates);
  const result = useMemo(() => {
    const node = system.nodes.find(n => n.id === nodeId);
    const def = node?.kind === "component" && getDefinition(node.definitionId, node.customDefinition);
    if (!def || !def.terminals.some(t => isAcTerminalId(t.id))) return null;
    const direction: "input" | "output" = def.category !== "distribution" && def.terminals.some(t => isAcTerminalId(t.id) && t.direction === "output")
      ? "output" : "input";
    const status = computeAcAvailability(system, getDefinition, { componentStates, loadStates })[direction].get(nodeId);
    return status ? { status, direction } : null;
  }, [system, componentStates, loadStates, nodeId]);
  if (!result) return null;
  return <div className="mt-1 px-2 text-center text-[10px] leading-tight"
    data-ac-supply={result.status} title={tr("simulate.ac.help")}
    style={{ color: result.status === "off" ? "var(--color-fg-muted)" : "var(--color-fg)" }}>
    {tr(`simulate.ac.${result.direction}`, { status: tr(`simulate.ac.${result.status}`) })}
  </div>;
}

function ChargerEngineStatus({ nodeId, def }: { nodeId: string; def: ComponentDefinition }) {
  const tr = useT();
  const system = useSystemStore(s => s.system);
  const componentStates = useSystemStore(s => s.simulateState.componentStates);
  const status = useMemo(() => {
    const node = system.nodes.find(n => n.id === nodeId);
    return node?.kind === "component"
      ? chargerEngineDetection(node, def, system, getDefinition, { componentStates }) : undefined;
  }, [system, componentStates, nodeId, def]);
  if (!status || status === "off") return null;
  return <div className="mt-1 px-2 text-center text-[10px] leading-tight text-fg" title={tr("simulate.orion.help")}>
    {tr(`simulate.orion.status_${status}`)}
  </div>;
}

/** Stops pointer/click bubbling — without it a pill tap also selects the node
 *  and slides the SelectionSheet over the card just tapped. */
function SegmentedStatePills({
  options,
  onSelect,
}: {
  options: Array<{ id: string; label: string; active: boolean; title?: string }>;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      className="nodrag nopan flex justify-center pointer-events-auto mt-1"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="inline-flex rounded-[5px] overflow-hidden"
        style={{ border: "1px solid var(--color-border)", background: "var(--color-surface)" }}
      >
        {options.map((o, i) => (
          <button
            key={o.id}
            type="button"
            aria-pressed={o.active}
            title={o.title}
            onClick={() => onSelect(o.id)}
            className="px-1.5 py-0.5 text-[10px] font-medium tracking-tight whitespace-nowrap min-w-0 truncate transition-colors"
            style={{
              background: o.active ? "var(--color-fg)" : "transparent",
              color: o.active ? "var(--color-surface)" : "var(--color-fg-muted)",
              borderLeft:
                i === 0 ? "none" : "1px solid var(--color-border)",
              cursor: "pointer",
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function BatterySocPanel({
  nodeId,
  def,
  displayName,
}: {
  nodeId: string;
  def: ComponentDefinition;
  displayName: string;
}) {
  const system = useSystemStore((s) => s.system);
  const componentStates = useSystemStore(
    (s) => s.simulateState.componentStates,
  );
  const loadStates = useSystemStore((s) => s.simulateState.loadStates);
  const storedSoc = useSystemStore((s) => s.simulateState.batterySoc[nodeId]);
  const setBatterySoc = useSystemStore((s) => s.setBatterySoc);
  const productImagesVisible = useProductImageVisibility((s) => s.visible);
  const viewOnly = useCanvasViewOnly();
  // Share state identities with the energy ledger used by every battery card.
  const simulate = useMemo(
    () => ({ componentStates, loadStates }),
    [componentStates, loadStates],
  );
  const node = system.nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "component") return null;
  const soc = storedSoc ?? DEFAULT_SOC;
  const pct = Math.round(soc * 100);
  const runtime = batteryRuntime(node, system, simulate, soc);
  const charging = runtime.netCurrentA > 0;
  const discharging = runtime.netCurrentA < 0;
  const active = charging || discharging;
  const timeValue = formatHours(
    charging ? runtime.timeToFullHours : runtime.timeToEmptyHours,
  );
  const directionArrow = charging ? "↑" : "↓";
  const amps = Math.abs(runtime.netCurrentA);
  const currentLabel = amps < 1 ? formatAmp(amps) : `${Math.round(amps)} A`;
  const imageUrl = productImagesVisible
    ? catalogImageUrl(resolveImageFilename(def))
    : null;
  return (
    <div
      className="nodrag nopan absolute inset-0 flex flex-col px-2.5 py-1.5 pointer-events-auto"
      onMouseDown={viewOnly ? undefined : (e) => e.stopPropagation()}
      onClick={viewOnly ? undefined : (e) => e.stopPropagation()}
    >
      <div className="flex items-baseline gap-2">
        <span
          className="flex-1 min-w-0 truncate text-[10.5px] leading-tight"
          style={{ color: "var(--color-fg-muted)" }}
          title={displayName}
        >
          {displayName}
        </span>
        <span
          className="flex-none text-[10px] leading-tight font-mono tabular-nums truncate"
          style={{ color: "var(--color-fg-subtle)" }}
        >
          {active ? (
            <>
              {directionArrow} {currentLabel}
              <span className="px-1" aria-hidden>
                ·
              </span>
              {timeValue}
            </>
          ) : (
            "idle"
          )}
        </span>
      </div>
      <div className="relative flex-1 min-h-0 my-0.5">
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            draggable={false}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          aria-label={`${displayName} charge`}
          aria-valuetext={`${pct}%`}
          min={0}
          max={100}
          step={1}
          value={pct}
          onChange={(e) => setBatterySoc(nodeId, Number(e.target.value) / 100)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="scenario-slider nodrag nopan flex-1 min-w-0 h-1.5 cursor-pointer appearance-none bg-transparent"
          style={{
            background: `linear-gradient(to right, var(--color-fg) 0%, var(--color-fg) ${pct}%, var(--color-border) ${pct}%, var(--color-border) 100%)`,
          }}
        />
        <span
          className="flex-none inline-flex items-baseline gap-0.5 font-mono tabular-nums"
          style={{ color: "var(--color-fg)" }}
        >
          <span className="text-[13px] leading-none" style={{ fontWeight: 500 }}>
            {pct}
          </span>
          <span
            className="text-[9.5px] tracking-wide"
            style={{ color: "var(--color-fg-subtle)" }}
          >
            %
          </span>
        </span>
      </div>
    </div>
  );
}
